import { UserManager, User, SignoutResponse, UserManagerSettings } from 'oidc-client';
import { from, Observable, Subject } from 'rxjs';
import { OidcContext } from '@shared/models/oidc-context';
import { map } from 'rxjs/operators';

export class OidcService {
   private userManager: UserManager;
   private user: User;
   private loginChangedSubject = new Subject<boolean>();

   public loginChanged = this.loginChangedSubject.asObservable();
   public authContext: OidcContext;

   constructor(private settings: UserManagerSettings) {
      this.userManager = new UserManager(this.settings);

      this.userManager.events.addAccessTokenExpired(_ => {
         this.loginChangedSubject.next(false);
      });

      this.userManager.events.addUserLoaded(user => {
         if (this.user !== user) {
            this.user = user;
            this.loginChangedSubject.next(!!user && !user.expired);
         }
      });
   }

   public login(): Promise<void> {
      return this.userManager.signinRedirect();
   }

   public isLoggedIn(): Promise<boolean> {
      return this.userManager.getUser().then(user => {
         const userCurrent = !!user && !user.expired;
         if (this.user !== user) {
            this.loginChangedSubject.next(userCurrent);
         }
         this.user = user;

         return userCurrent;
      });
   }

   public isLoggedInObservable(): Observable<boolean> 
   {
      return from(this.userManager.getUser()).pipe(
         map(user => {
            const userCurrent = !!user && !user.expired;
            if (this.user !== user) {
               this.loginChangedSubject.next(userCurrent);
            }
            this.user = user;

            return userCurrent;
         })
      );
   }


   public completeLogin(): Promise<User> {
      return this.userManager.signinRedirectCallback().then(user => {
         this.user = user;
         this.loginChangedSubject.next(!!user && !user.expired);
         return user;
      });
   }

   public logout(): Promise<void> {
      return this.userManager.signoutRedirect();
   }

   public logoutObservable(): Observable<void> {
      return from(this.userManager.signoutRedirect());
   }

   public completeLogout(): Promise<SignoutResponse> {
      this.user = null;
      this.loginChangedSubject.next(false);

      return this.userManager.signoutRedirectCallback();
   }

   public getAccessToken(): Promise<string> {
      return this.userManager.getUser().then(user => !!user && !user.expired ? user.access_token : null);
   }

   public getAccessTokenObservable(): Observable<string> {
      return from(this.userManager.getUser().then(user => !!user && !user.expired ? user.access_token : null));
   }

   public getGroups(): Promise<Array<string>> {
      return this.userManager.getUser().then(user => !!user && !user.expired ? user.profile.groups : null);
   }

   public getGroupsObservable(): Observable<Array<string>> {
      return from(this.userManager.getUser().then(user => !!user && !user.expired ? user.profile.groups : null));
   }

   public setState(key: string, value: any): void {
      this.userManager.settings.stateStore.set(key, value);
   }

   public getState(key: string): Promise<any> {
      return this.userManager.settings.stateStore.get(key);
   }
}
